import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  CheckCircle2,
  Clock,
  Edit3,
  User,
  MapPin,
  Phone,
  X,
  ChevronDown,
  Search,
  RefreshCw,
  Users,
  ShieldCheck,
  ShieldAlert,
  Mail,
  IdCard,
  GraduationCap,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminHeader from '@/components/common/AdminHeader';
import { FormField } from '@/components/ui/FormField';
import DatePicker from '@/components/ui/DatePicker';
import Dropdown from '@/components/ui/Dropdown';
import { getVerificationList, updateChild, updateChildVerification } from '@/services/parent';
import { getVerificationByBranch } from '@/services/branchIncharge';
import { getBranchesByStateAndCity, getMasterDropdownService, getCitiesBasedOnState } from '@/services/masters';
import { colors, fontSize, fontWeight, textColor, bgColor, borderColor } from '@/theme';
import { useRoleContext } from '@/hooks/useRoleContext';
import { useScreenPermission } from '@/hooks/useScreenPermission';
import Pagination from '../common/Pagination';
import storage from '@/lib/storage';

function calculateEndDate(startDate: string, period: string, isTrial: boolean = false): string {
  if (!startDate) return '';
  const date = new Date(startDate);
  if (isNaN(date.getTime())) return '';
  if (isTrial) {
    date.setDate(date.getDate() + 2);   // trial is always 2 days
    return date.toISOString().split('T')[0];
  }

  if (!period) return '';
  const days =
    period === '1 Month' ? 30
      : period === '3 Months' ? 90
        : period === '6 Months' ? 180
          : 330;
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
}

const editChildSchema = z.object({
  Child_Name: z.string().min(1, 'Child name is required'),
  Age: z.string().min(1, 'Age is required'),
  Class: z.string().min(1, 'Class is required'),
  Section: z.string().optional(),
  Roll_No: z.string().optional(),
  Branch_Code: z.string().min(1, 'Branch is required'),
  Verification_Status: z.boolean(),
  Subscription_Start_Date: z.string().min(1, 'Start date is required'),
  Subscription_End_Date: z.string().min(1, 'End date is required'),
  Remarks: z.string().optional(),
});

type EditChildFormData = z.infer<typeof editChildSchema>;

function InfoPill({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | number;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <span className={`mt-0.5 flex-shrink-0 ${textColor.muted}`}>{icon}</span>
      <div>
        <p className={`${fontSize.xs} ${textColor.muted} leading-tight`}>{label}</p>
        <p className={`${fontSize.sm} ${fontWeight.medium} ${textColor.body}`}>{value}</p>
      </div>
    </div>
  );
}

function EditChildModal({
  child,
  onClose,
  onSuccess,
}: {
  child: any;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const period = child.Range?.Period || '';
  const initialStart = child.From_Date || '';
  const isTrial = !!child.Trail;
  const [branches, setBranches] = useState<any[]>([]);
  const [loadingBranches, setLoadingBranches] = useState(false);

  useEffect(() => {
    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        // Convert state/city names to unique_codes before calling the API
        let stateCode = child.State || '';
        let cityCode = child.City || '';

        const masterRes = await getMasterDropdownService();
        if (masterRes.status === 200 && masterRes.data) {
          const rawStates: any[] = (masterRes.data as any).States || [];
          stateCode = rawStates.find((s: any) => s.Name === child.State)?.unique_code
            || rawStates.find((s: any) => s.unique_code === child.State)?.unique_code
            || child.State || '';

          if (stateCode) {
            const citiesRes = await getCitiesBasedOnState(stateCode);
            if (citiesRes.status === 200 && citiesRes.data) {
              const rawCities: any[] = (citiesRes.data as any).Cities || [];
              const cityList = rawCities.map((c: any, idx: number) => ({
                id: typeof c === 'object' ? (c.id ?? idx) : idx,
                label: typeof c === 'object' ? c.City_Name : c,
                value: typeof c === 'object' ? (c.unique_code || c.City_Name) : c,
              }));
              cityCode = cityList.find((c) => c.label === child.City || c.value === child.City)?.value || child.City || '';
            }
          }
        }

        const response = await getBranchesByStateAndCity(stateCode, cityCode);
        if (response.status === 200 && response.data) {
          const d = response.data as any;
          const items = d.Branches ?? (Array.isArray(d) ? d : []);
          setBranches(items);
        }
      } catch (error) {
        console.error('Failed to load branches:', error);
      } finally {
        setLoadingBranches(false);
      }
    };
    fetchBranches();
  }, []);

  const branchOptions = branches.map((b: any, idx: number) => {
    const fullLabel = b.Branch_Address ? `${b.Branch_Name} - ${b.Branch_Address}` : b.Branch_Name;
    return {
      id: b.id ?? idx,
      label: fullLabel.length > 50 ? `${fullLabel.slice(0, 50)}…` : fullLabel,
      value: b.unique_code || b.Branch_Code,
    };
  });

  const {
    control,
    handleSubmit,
    clearErrors,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = useForm<EditChildFormData>({
    resolver: zodResolver(editChildSchema),
    defaultValues: {
      Child_Name: child.Child_Name || '',
      Age: child.Age?.toString() || '',
      Class: child.Class || '',
      Section: child.Section || '',
      Roll_No: child.Roll_No || '',
      Branch_Code: child.Branch_Code || '',
      Verification_Status: child.Verification_Status ?? false,
      Subscription_Start_Date: initialStart,
      Subscription_End_Date: calculateEndDate(initialStart, period, isTrial),
      Remarks: '',
    },
  });

  const verifyChecked = watch('Verification_Status');
  const watchedStartDate = watch('Subscription_Start_Date');

  // Auto-recalculate end date when start date changes
  useEffect(() => {
    if (watchedStartDate && (isTrial || period)) {
      setValue('Subscription_End_Date', calculateEndDate(watchedStartDate, period, isTrial));
    }
  }, [watchedStartDate, period, isTrial, setValue]);

  const onSubmit = async (data: EditChildFormData) => {
    const changed: Record<string, any> = {};
    if (data.Child_Name !== (child.Child_Name || '')) changed.Child_Name = data.Child_Name;
    if (data.Age !== (child.Age?.toString() || '')) changed.Age = parseInt(data.Age);
    if (data.Class !== (child.Class || '')) changed.Class = data.Class;
    if ((data.Section || '') !== (child.Section || '')) changed.Section = data.Section;
    if ((data.Roll_No || '') !== (child.Roll_No || '')) changed.Roll_No = data.Roll_No;
    const payload = {
      ...changed,
      Verification_Status: data.Verification_Status,
      Subscription_Start_Date: data.Subscription_Start_Date,
      Subscription_End_Date: data.Subscription_End_Date,
      Remarks: data.Remarks || '',
      Branch_Code: data.Branch_Code,
    };

    try {
      const response = await updateChildVerification(child.id, payload);
      if (response.status === 200 || response.status === 201) {
        toast.success(
          data.Verification_Status ? 'Child verified successfully!' : 'Child updated successfully'
        );
        onSuccess();
      } else if (response.errors && typeof response.errors === 'object') {
        Object.entries(response.errors).forEach(([, value]) => {
          const msg = Array.isArray(value) ? value[0] : String(value);
          toast.error(msg);
        });
      } else {
        toast.error('Failed to update child');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error updating child');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`${bgColor.white} rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden`}>
        <div
          className="flex items-center justify-between px-6 py-4 rounded-t-2xl"
          style={{ background: colors.brand.gradient }}
        >
          <div className="flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-white" />
            <h3 className={`${fontSize.lg} ${fontWeight.bold} ${textColor.white}`}>
              Edit Child Details
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto flex-1">
          <FormField
            control={control}
            name="Child_Name"
            label="Child Name"
            placeholder="Enter child name"
            required
            error={errors.Child_Name?.message}
            onClear={() => clearErrors('Child_Name')}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={control}
              name="Age"
              label="Age"
              type="text"
              placeholder="Age"
              required
              error={errors.Age?.message}
              onClear={() => clearErrors('Age')}
            />
            <FormField
              control={control}
              name="Class"
              label="Class"
              placeholder="e.g. 3rd"
              required
              error={errors.Class?.message}
              onClear={() => clearErrors('Class')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={control}
              name="Section"
              label="Section"
              placeholder="e.g. A"
              error={errors.Section?.message}
              onClear={() => clearErrors('Section')}
            />
            <FormField
              control={control}
              name="Roll_No"
              label="Roll No"
              placeholder="Roll number"
              error={errors.Roll_No?.message}
              onClear={() => clearErrors('Roll_No')}
            />
          </div>

          <Dropdown
            name="Branch_Code"
            control={control}
            label="Branch"
            options={branchOptions}
            placeholder="Select branch"
            isLoading={loadingBranches}
            required
            error={errors.Branch_Code?.message}
            onClear={() => clearErrors('Branch_Code')}
          />

          {/* Subscription dates */}
          <div className="grid grid-cols-2 gap-4">
            <DatePicker
              name="Subscription_Start_Date"
              control={control}
              label="Start Date"
              required
              error={errors.Subscription_Start_Date?.message}
            />
            <DatePicker
              name="Subscription_End_Date"
              control={control}
              label="End Date"
              required
              disabled
              error={errors.Subscription_End_Date?.message}
            />
          </div>
          {isTrial && (
            <p className={`${fontSize.xs} ${textColor.muted} -mt-2`}>
              Trial subscription — end date is fixed to 2 days from the start date.
            </p>
          )}
          {/* Remarks */}
          <FormField
            control={control}
            name="Remarks"
            label="Remarks"
            placeholder="Enter remarks"
            isTextarea
            rows={3}
            error={errors.Remarks?.message}
            onClear={() => clearErrors('Remarks')}
          />

          <div
            className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${verifyChecked ? 'bg-green-50 border-green-400' : `${bgColor.gray50} border-gray-200`
              }`}
            onClick={() => setValue('Verification_Status', !verifyChecked)}
          >
            <div
              className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${verifyChecked ? 'bg-green-500 border-green-500' : 'border-gray-300 bg-white'
                }`}
            >
              {verifyChecked && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
            </div>
            <div>
              <p
                className={`${fontSize.sm} ${fontWeight.semibold} ${verifyChecked ? 'text-green-700' : textColor.heading
                  }`}
              >
                Mark as Verified
              </p>
              <p
                className={`${fontSize.xs} ${verifyChecked ? 'text-green-600' : textColor.muted
                  }`}
              >
                {verifyChecked
                  ? 'This child will be marked as verified'
                  : 'Check to approve and verify this child'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`flex gap-3 px-6 py-4 border-t ${borderColor.gray200}`}>
          <button
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isSubmitting}
            className={`flex-1 py-2.5 rounded-xl ${fontWeight.semibold} ${fontSize.sm} ${textColor.white} transition-all disabled:opacity-50`}
            style={{ background: colors.brand.gradient }}
          >
            {isSubmitting ? 'Saving…' : 'Save Changes'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={`flex-1 py-2.5 ${bgColor.gray100} hover:bg-gray-200 ${textColor.heading} ${fontWeight.semibold} ${fontSize.sm} rounded-xl transition-colors disabled:opacity-50`}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function ChildCard({ child, onEdit, canUpdate }: { child: any; onEdit: (c: any) => void; canUpdate: boolean }) {
  const verified = child.Verification_Status;
     const userData = storage.getUserData();
     const isSuperUser = userData?.isSuperuser
  return (
    <div
      className={`rounded-xl border p-4 transition-all ${verified ? 'border-green-200 bg-green-50/40' : 'border-orange-200 bg-orange-50/40'
        }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-1.5">
          {/* Name + badge */}
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className={`${fontWeight.bold} ${fontSize.base} ${textColor.heading}`}>
              {child.Child_Name}
            </h4>
            {verified ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                <CheckCircle2 className="w-3 h-3" /> Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                <Clock className="w-3 h-3" /> Pending
              </span>
            )}
            {child.School_Status === 'Approved' ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                <CheckCircle2 className="w-3 h-3" />School Status : Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                <Clock className="w-3 h-3" /> School Status : Pending
              </span>
            )}
            {child.payment_status === 'Paid' ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                <CheckCircle2 className="w-3 h-3" />Payment Status : Paid
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                <Clock className="w-3 h-3" /> Payment Status : Pending
              </span>
            )}
          </div>

          {/* Core info */}
          <div className="flex flex-wrap gap-x-4 gap-y-0.5">
            <span className={`${fontSize.sm} ${textColor.muted}`}>
              Age:{' '}
              <span className={`${fontWeight.medium} ${textColor.body}`}>{child.Age}</span>
            </span>
            <span className={`${fontSize.sm} ${textColor.muted}`}>
              Class:{' '}
              <span className={`${fontWeight.medium} ${textColor.body}`}>{child.Class}</span>
            </span>
            {child.Section && (
              <span className={`${fontSize.sm} ${textColor.muted}`}>
                Sec:{' '}
                <span className={`${fontWeight.medium} ${textColor.body}`}>{child.Section}</span>
              </span>
            )}
            {child.Roll_No && (
              <span className={`${fontSize.sm} ${textColor.muted}`}>
                Roll:{' '}
                <span className={`${fontWeight.medium} ${textColor.body}`}>{child.Roll_No}</span>
              </span>
            )}
          </div>

          {/* School */}
          {child.School && (
            <p className={`${fontSize.sm} ${textColor.muted} flex items-center gap-1`}>
              <GraduationCap className="w-3.5 h-3.5 flex-shrink-0" />
              {child.School}
              {child.City ? ` — ${child.City}` : ''}
              {child.State ? ` — ${child.State}` : ''}
            </p>
          )}

          {/* Plan */}
          {child.Plan_Name && (
            <p className={`${fontSize.sm} ${textColor.muted}`}>
              Plan:{' '}
              <span className={`${fontWeight.medium} ${textColor.body}`}>{child.Plan_Name}</span>
              {child.Range?.Period && (
                <span className={textColor.muted}> · {child.Range.Period}</span>
              )}
              {child.Range?.Final_Amount && (
                <span className={textColor.muted}> · ₹{child.Range?.Final_Amount !== undefined &&
                  child.Range?.Final_Amount !== null
                  ? ` · ₹${parseFloat(String(child.Range.Final_Amount)).toFixed(2)}`
                  : ''}</span>
              )}
            </p>
          )}
        </div>

        {canUpdate && (
          <button
            onClick={() => onEdit(child)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-500 text-white text-sm font-semibold rounded-lg hover:bg-brand-700 transition-colors flex-shrink-0"
          >
            <Edit3 className="w-3.5 h-3.5" />
            Edit
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Parent Card ──────────────────────────────────────────────────────────────
function ParentCard({
  parent,
  onEditChild,
  canUpdate,
}: {
  parent: any;
  onEditChild: (c: any) => void;
  canUpdate: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const children: any[] = parent.Childs || parent.childs || [];
  const verifiedCount = children.filter((c) => c.Verification_Status).length;
  const pendingCount = children.length - verifiedCount;

  return (
    <div
      className={`${bgColor.white} rounded-2xl shadow-sm border ${borderColor.gray200} overflow-hidden`}
    >
      {/* Accordion trigger */}
      <div
        className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {/* Avatar */}
            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 border border-gray-200 bg-gray-100">
              {parent.Profile_Image ? (
                <img
                  src={parent.Profile_Image}
                  alt={parent.Username}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-white text-lg font-bold"
                  style={{ background: colors.brand.gradient }}
                >
                  {(parent.First_Name || parent.Username || '?')[0].toUpperCase()}
                </div>
              )}
            </div>

            {/* Name */}
            <div>
              <p className={`${fontWeight.bold} ${fontSize.base} ${textColor.heading}`}>
                {parent.First_Name && parent.Last_Name
                  ? `${parent.First_Name} ${parent.Last_Name}`
                  : parent.Username}
              </p>
              <p className={`${fontSize.sm} ${textColor.muted}`}>@{parent.Username}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              {verifiedCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                  <CheckCircle2 className="w-3 h-3" /> {verifiedCount} verified
                </span>
              )}
              {pendingCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                  <Clock className="w-3 h-3" /> {pendingCount} pending
                </span>
              )}
            </div>
            <ChevronDown
              className={`w-5 h-5 ${textColor.muted} transition-transform duration-200 ${expanded ? 'rotate-180' : ''
                }`}
            />
          </div>
        </div>
      </div>

      {/* Body */}
      {expanded && (
        <div className={`border-t ${borderColor.gray200} px-6 py-5 space-y-5`}>
          {/* Parent info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl">
            <InfoPill
              icon={<Mail className="w-4 h-4" />}
              label="Email"
              value={parent.Email || parent.email}
            />
            <InfoPill icon={<Phone className="w-4 h-4" />} label="Phone" value={parent.Phone} />
            <InfoPill
              icon={<Phone className="w-4 h-4" />}
              label="Alt Phone"
              value={parent.Alternative_Phone}
            />
            <InfoPill
              icon={<MapPin className="w-4 h-4" />}
              label="Address"
              value={parent.Address}
            />
            <InfoPill
              icon={<MapPin className="w-4 h-4" />}
              label="Latitude"
              value={parent.Latitude}
            />
            <InfoPill
              icon={<MapPin className="w-4 h-4" />}
              label="Longitude"
              value={parent.Longitude}
            />
          </div>

          {/* Proof image */}
          {parent.Proof_Image && (
            <div>
              <p
                className={`${fontSize.xs} ${fontWeight.semibold} ${textColor.muted} mb-2 uppercase tracking-wider flex items-center gap-1`}
              >
                <IdCard className="w-3.5 h-3.5" /> ID / Proof Document
              </p>
              <img
                src={parent.Proof_Image}
                alt="Proof"
                className="h-28 rounded-lg object-cover border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => window.open(parent.Proof_Image, '_blank')}
              />
            </div>
          )}

          {/* Children */}
          <div>
            <p className={`${fontSize.sm} ${fontWeight.semibold} ${textColor.secondary} mb-3`}>
              Children ({children.length})
            </p>
            {children.length === 0 ? (
              <p className={`${textColor.muted} ${fontSize.sm} text-center py-4`}>
                No children registered
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {children.map((child: any, ci: number) => (
                  <ChildCard
                    key={`${child.id ?? child.Child_Name}-${ci}`}
                    child={child}
                    onEdit={(c) => onEditChild({ ...c, _parentState: parent.State, _parentCity: parent.City })}
                    canUpdate={canUpdate}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

type StatusTab = 'pending' | 'verified';

export default function VerificationComponent() {
  const { isBranchIncharge, branchCode } = useRoleContext();
  const { canUpdate } = useScreenPermission('verified child');
  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<StatusTab>('pending');
  const [editingChild, setEditingChild] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState<{ parents: number; children: number; verified: number; pending: number } | null>(null)
  const searchMounted = useRef(false);

  const load = useCallback(async (p: number = 1, q: string = "", tab: StatusTab = 'pending') => {
    setLoading(true);
    try {
      const isVerified = tab === 'verified';
      const response = isBranchIncharge && branchCode
        ? await getVerificationByBranch(branchCode, isVerified ? 'True' : 'False', p)
        : await getVerificationList(p, q || undefined, isVerified ? 'True' : 'False');
      if (response.status === 200 && response.data) {
        const data = response.data as any;
        const items = data.results ?? (Array.isArray(data) ? data : []);
        setRecords(items);
        setCounts(data.counts)
        setTotalPages(data.total_pages ?? 1);
        setTotalCount(data.count ?? items.length);
      } else {
        // toast.error('Failed to load verification data');
      }
    } catch (err) {
      console.error('Verification load error:', err);
      // toast.error('Error loading verification data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + tab change
  useEffect(() => {
    setPage(1);
    load(1, search, activeTab);
  }, [activeTab]);

  // Debounced backend search (skip initial mount)
  useEffect(() => {
    if (!searchMounted.current) { searchMounted.current = true; return; }
    const id = setTimeout(() => {
      setPage(1);
      load(1, search, activeTab);
    }, 500);
    return () => clearTimeout(id);
  }, [search]);

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-6">

        {/* Page hero */}
        <div
          className="rounded-2xl p-6 text-white shadow-lg"
          style={{ background: colors.brand.gradient }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className={`${fontSize['3xl']} ${fontWeight.bold} text-white`}>
                Verification Centre
              </h1>
              <p className="text-white/80 mt-1 text-sm">
                Review and verify registered parents and children
              </p>
            </div>
            <button
              onClick={() => load(page, search, activeTab)}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-semibold text-sm rounded-xl transition-colors disabled:opacity-50 self-start sm:self-auto"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Stat chips */}
          {!loading && (
            <div className="flex flex-wrap gap-3 mt-5">
              <div className="flex items-center gap-2 bg-white/15 px-3 py-2 rounded-xl">
                <Users className="w-4 h-4 text-white/80" />
                <span className="text-sm font-semibold">{counts?.parents} Parents</span>
              </div>
              <div className="flex items-center gap-2 bg-white/15 px-3 py-2 rounded-xl">
                <User className="w-4 h-4 text-white/80" />
                <span className="text-sm font-semibold">{counts?.children} Children</span>
              </div>
              <div className="flex items-center gap-2 bg-green-500/30 px-3 py-2 rounded-xl">
                <ShieldCheck className="w-4 h-4 text-green-200" />
                <span className="text-sm font-semibold text-green-100">
                  {counts?.verified} Verified
                </span>
              </div>
              <div className="flex items-center gap-2 bg-orange-500/30 px-3 py-2 rounded-xl">
                <ShieldAlert className="w-4 h-4 text-orange-200" />
                <span className="text-sm font-semibold text-orange-100">
                  {counts?.pending} Pending
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Status Tabs */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setActiveTab('pending')}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all border ${activeTab === 'pending'
                ? 'bg-orange-500 text-white border-orange-500 shadow-md'
                : `${bgColor.white} ${textColor.body} ${borderColor.gray200} hover:bg-gray-50`
              }`}
          >
            <ShieldAlert className="w-4 h-4" />
            Pending
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === 'pending' ? 'bg-white/20 text-white' : 'bg-orange-100 text-orange-600'}`}>
              {counts?.pending ?? 0}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('verified')}
            className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all border ${activeTab === 'verified'
                ? 'bg-green-600 text-white border-green-600 shadow-md'
                : `${bgColor.white} ${textColor.body} ${borderColor.gray200} hover:bg-gray-50`
              }`}
          >
            <ShieldCheck className="w-4 h-4" />
            Verified
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${activeTab === 'verified' ? 'bg-white/20 text-white' : 'bg-green-100 text-green-600'}`}>
              {counts?.verified ?? 0}
            </span>
          </button>
        </div>

        {/* Search bar */}
        <div className="relative">
          <Search
            className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${textColor.muted}`}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or username…"
            className={`w-full pl-10 pr-10 py-3 ${bgColor.white} border ${borderColor.gray200} rounded-xl ${fontSize.sm} ${textColor.body} focus:outline-none focus:ring-2 focus:ring-brand-400 shadow-sm`}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className={`absolute right-3.5 top-1/2 -translate-y-1/2 ${textColor.muted} hover:text-gray-700`}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div
            className={`${bgColor.white} rounded-2xl border ${borderColor.gray200} p-16 text-center shadow-sm`}
          >
            <div
              className="w-12 h-12 rounded-full border-4 border-t-transparent mx-auto mb-4 animate-spin"
              style={{
                borderColor: `${colors.brand.primary} transparent transparent transparent`,
              }}
            />
            <p className={`${textColor.muted} ${fontWeight.medium}`}>
              Loading verification data…
            </p>
          </div>
        ) : records.length === 0 ? (
          <div
            className={`${bgColor.white} rounded-2xl border ${borderColor.gray200} p-16 text-center shadow-sm`}
          >
            <ShieldCheck className={`w-12 h-12 mx-auto mb-4 ${textColor.muted}`} />
            <p className={`${fontSize.lg} ${fontWeight.semibold} ${textColor.heading} mb-1`}>
              {search ? 'No results found' : activeTab === 'verified' ? 'No verified records' : 'No pending records'}
            </p>
            <p className={`${textColor.muted} ${fontSize.sm}`}>
              {search
                ? `No parents match "${search}"`
                : activeTab === 'verified' ? 'No parents have been verified yet'
                  : 'All parents are verified!'}
            </p>
          </div>
        ) : (
          <>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalCount={totalCount}
              itemsPerPage={20}
              onPageChange={(p) => { setPage(p); load(p, search, activeTab); }}
            />
            <div className="space-y-4">
              {records.map((parent: any, pi: number) => (
                <ParentCard
                  key={`${parent.id ?? parent.Username}-${pi}`}
                  parent={parent}
                  onEditChild={setEditingChild}
                  canUpdate={canUpdate}
                />
              ))}
            </div>
          </>
        )}
      </main>

      {editingChild && (
        <EditChildModal
          child={editingChild}
          onClose={() => setEditingChild(null)}
          onSuccess={() => {
            setEditingChild(null);
            load(page, search, activeTab);
          }}
        />
      )}
    </div>
  );
}
